import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { IListFilterItem, ListFilterItem } from "source/models/common/listFilterItem";
import { ITypedOption } from "source/models/common/typedOption";
import * as companyGroupService from "source/models/services/companyGroupService";
import { IAppSessionState } from "source/models/store/storeTypes";
import { IUnitNodePermissions } from "source/models/unit/unitNodePermissions";
import { HeaderMenuItem, NavigationMenu } from "source/components/app/NavigationMenu";
import { UserMenu } from "source/components/app/UserMenu";
import { PageContainer } from "source/components/framework/Page/PageContainer";
import { PageContent } from "source/components/framework/Page/PageContent";
import { PageTopBar } from "source/components/framework/Page/PageTopBar";
import { FilterTabs } from "source/components/framework/PageNavMenuItem/FilterTabs";
import { IPageNavMenuItemActionsAction } from "source/components/framework/PageNavMenuItem/Actions";
import { ICompanyGroupPage } from "source/models/companyGroup/companyGroupPage";
import { CompanyGroupList } from "../CompanyGroupList";
import { AppUtils } from "source/framework/appUtils";
import { UnitPermissionDialog } from "source/components/unit/Unit/UnitPermissionDialog";
import { abortManager } from "source/models/services/tokenService";

// CompanyPage
export interface ICompanyGroupPageOwnProps {
}

export interface ICompanyGroupPageStateProps {
    appState: IAppSessionState;
    dataId: number;
    companyGroupPage: ICompanyGroupPage;
}

export interface ICompanyGroupPageDispatchProps {
    onGetCompanyGroupsPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

type CompanyGroupPageProp = ICompanyGroupPageOwnProps & ICompanyGroupPageStateProps & ICompanyGroupPageDispatchProps;

interface ICompanyGroupPageState {
    initialized: boolean;
    showNavPanel: boolean;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItemActionsAction[];
    // Filters
    companyGroupFilterIsSet: boolean;
    showCompanyGroupFilterDialog: boolean;
    companyGroupFilterDialogFilter: IListFilterItem;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    activeFilterId: string;
    activeSavedFilterId: string;
    showListFilterSaveDialog: boolean;
    listFilterSaveDialogFilter: IListFilterItem;
    listFilterSaveDialogGeneralFilter: any;
    listFilterSaveDialogReservedNames: string[];
    listFilterSaveDialogOnGetGeneralFilter: () => any;
    // Properties
    dataId: number;
    showUnitNodePermissionsDialog: boolean;
    unitNodePermissions: IUnitNodePermissions;
}

export class CompanyGroupPage extends React.Component<CompanyGroupPageProp, ICompanyGroupPageState> {
    getFilters = (): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        result.push(ListFilterItem.createListFilterItem(Translations.CompanyGroups, Translations.CompanyGroups,
            {
            }, "name", true));
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getSavedFilters = (props: CompanyGroupPageProp): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        if (!props || !props.companyGroupPage) return result;
        for (const companyFilter of props.companyGroupPage.companyFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(companyFilter, true));
        }
        for (const userFilter of props.companyGroupPage.userFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(userFilter, false));
        }
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getCompanyGroupFilterIsSet = (state: ICompanyGroupPageState): boolean => {
        return false;
        // return CompanyGroupFilterDrawer.filterIsSet(state.authorizationLevelFilters,
        //     state.authorizationLevelId);
    };

    getSettingsActions = (): IPageNavMenuItemActionsAction[] => {
        const props = this.props;
        const sessionState = props.appState.state;
        const result: IPageNavMenuItemActionsAction[] = [];
        if (sessionState.isCompanyAdmin) {
            result.push({ title: Translations.Permissions, onClick: this.handleOpenPermissions });
        }
        return result;
    };

    constructor(props: CompanyGroupPageProp) {
        super(props);
        abortManager.abortAll(); //Abort all abortable calls regardless of module
        const sessionState = props.appState.state;
        const filters = this.getFilters();
        const filterProps = ListFilterItem.getFilterProps(filters[0], "", true, this.getStateFilterPropsFromGeneralFilter);
        const state = {
            ...filterProps,
            initialized: !!props.companyGroupPage,
            showNavPanel: sessionState.getShowPageNavigation(),
            activeCompanyId: sessionState.companyId,
            settingsActions: this.getSettingsActions(),
            // Filters
            companyGroupFilterIsSet: false,
            activeFilterId: filters[0].id,
            activeSavedFilterId: "",
            filters,
            savedFilters: this.getSavedFilters(props),
            showCompanyGroupFilterDialog: false,
            companyGroupFilterDialogFilter: null,
            showListFilterSaveDialog: false,
            listFilterSaveDialogFilter: null,
            listFilterSaveDialogGeneralFilter: null,
            listFilterSaveDialogReservedNames: [],
            listFilterSaveDialogOnGetGeneralFilter: null,
            // Properties
            dataId: props.dataId,
            showUnitNodePermissionsDialog: false,
            unitNodePermissions: null
        };
        this.state = {
            ...state,
            companyGroupFilterIsSet: this.getCompanyGroupFilterIsSet(state)
        };
    }

    componentDidMount(): void {
        this.props.onGetCompanyGroupsPage();
    }

    componentDidUpdate(prevProps: CompanyGroupPageProp, prevState: ICompanyGroupPageState): void {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        if (prevProps.dataId === props.dataId) return;
        const savedFilters = this.getSavedFilters(props);
        const activeSavedFilterId = savedFilters.find(i => i.id === state.activeSavedFilterId)
            ? state.activeSavedFilterId
            : "";
        const stateChange = {
            dataId: props.dataId,
            initialized: !!props.companyGroupPage,
            showNavPanel: state.initialized ? state.showNavPanel : sessionState.getShowPageNavigation(),
            savedFilters,
            activeSavedFilterId
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            companyGroupFilterIsSet: this.getCompanyGroupFilterIsSet(newState),
            settingsActions: this.getSettingsActions(),
        });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };
    
    // handleSetAuthorizationLevelId = (authorizationLevelId: string, checked: boolean) => {
    //     if (!checked) return;
    //     this.setState({ authorizationLevelId });
    // };

    // #region Saved Filter
    getGeneralFilterFromState = (state: ICompanyGroupPageState): any => {
        return {
            //authorizationLevelId: state.authorizationLevelId ? parseInt(state.authorizationLevelId, 10) : null
        };
    };

    getStateFilterPropsFromGeneralFilter = (generalFilter: any): any => {
        return {
            //authorizationLevelId: generalFilter && !Base.isNullOrUndefined(generalFilter.authorizationLevelId) && !isNaN(generalFilter.authorizationLevelId) ? generalFilter.authorizationLevelId.toString(10) : ""
        };
    };
    
    getFilterPropsWithState = (listItemFilter: IListFilterItem) => {
        const state = this.state;
        let result = {
            sortColumn: state.sortColumn,
            sortOrderIsAsc: state.sortOrderIsAsc
        };
        if (listItemFilter) {
            result = ListFilterItem.getFilterProps(listItemFilter, result.sortColumn, result.sortOrderIsAsc, this.getStateFilterPropsFromGeneralFilter);
        }
        return result;
    };

    handleChangeActiveFilter = (activeFilterId: string) => {
        const state = this.state;
        const filter = state.filters.find(i => i.id === activeFilterId);
        if (!filter) return;
        const filterProps = this.getFilterPropsWithState(filter);
        const activeSavedFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId: "",
            companyGroupFilterIsSet: this.getCompanyGroupFilterIsSet(newState)
        });
    };

    handleChangeActiveSavedFilter = (activeSavedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === activeSavedFilterId);
        if (!savedFilter) return;
        const filterProps = this.getFilterPropsWithState(savedFilter);
        const activeFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId,
            companyGroupFilterIsSet: this.getCompanyGroupFilterIsSet(newState)
        });
    };

    // handleRemoveSavedFilter = async (savedFilterId: string) => {
    //     const state = this.state;
    //     const savedFilter = state.savedFilters.find(i => i.id === savedFilterId);
    //     if (!savedFilter) return;
    //     const activeCompanyId = this.props.appState.state.companyId;
    //     const result = await ListFilterSaveDialog.removeListFilterFromDb(state.savedFilters, activeCompanyId, savedFilterId, savedFilter.isShared, ApplicationPermissionType.CompanyGroup);
    //     if (!result) return;
    //     const savedFilters = state.savedFilters.filter(i => i.id !== savedFilterId);
    //     let activeFilterId = "";
    //     let activeSavedFilterId = "";
    //     let newfilter: IListFilterItem = null;
    //     if (savedFilters.length > 0) {
    //         newfilter = savedFilters[0];
    //         activeSavedFilterId = newfilter.id;
    //     } else if (state.filters.length > 0) {
    //         newfilter = state.filters[0];
    //         activeFilterId = newfilter.id;
    //     }
    //     const filterProps = this.getFilterPropsWithState(newfilter);
    //     const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId, savedFilters };
    //     this.setState({
    //         ...filterProps,
    //         showCompanyGroupFilterDialog: false,
    //         activeFilterId,
    //         activeSavedFilterId,
    //         savedFilters,
    //         companyGroupFilterIsSet: this.getCompanyGroupFilterIsSet(newState)
    //     });
    // };

    handleFilterSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            sortColumn,
            sortOrderIsAsc,
        });
    };

    // updateFiltersAfterSave = (orgFilterId: string, savedListFilter: ISavedListFilter, isShared: boolean) => {
    //     if (!savedListFilter) return;
    //     const state = this.state;
    //     const savedFilters = state.savedFilters.filter(i => i.id !== orgFilterId || i.isShared !== isShared);
    //     const filter = ListFilterItem.createListFilterItemFromSavedListFilter(savedListFilter, isShared);
    //     savedFilters.push(filter);
    //     ListFilterItem.sortListFilterItems(savedFilters);
    //     this.setState({
    //         showListFilterSaveDialog: false,
    //         savedFilters,
    //         activeFilterId: "",
    //         activeSavedFilterId: savedListFilter.id,
    //         companyGroupFilterDialogFilter: filter
    //     });
    // };

    // getActiveFilter = (): IListFilterItem => {
    //     const state = this.state;
    //     return state.activeFilterId
    //         ? state.filters.find(i => i.id === state.activeFilterId)
    //         : state.savedFilters.find(i => i.id === state.activeSavedFilterId);
    // };

    // handleSaveListFilter = async (saveAs: boolean, onGetGeneralFilter: () => any) => {
    //     const state = this.state;
    //     const filter = this.getActiveFilter();
    //     if (!filter) return;
    //     if (!saveAs) {
    //         const activeCompanyId = this.props.appState.state.companyId;
    //         const savedListFilter = await ListFilterSaveDialog.saveListFilterToDb(filter, this.getGeneralFilterFromState(state), state.sortColumn, state.sortOrderIsAsc, activeCompanyId, ApplicationPermissionType.CompanyGroup);
    //         this.updateFiltersAfterSave(filter.id, savedListFilter, filter.isShared);
    //     } else {
    //         this.setState({
    //             showListFilterSaveDialog: true,
    //             listFilterSaveDialogFilter: filter,
    //             listFilterSaveDialogReservedNames: state.savedFilters.map(i => i.id).concat(state.filters.map(i => i.title)),
    //             listFilterSaveDialogGeneralFilter: this.getGeneralFilterFromState(state),
    //             listFilterSaveDialogOnGetGeneralFilter: onGetGeneralFilter
    //         });
    //     }
    // };

    // handleSaveListFilterDialogOk = (savedListFilter: ISavedListFilter, isShared: boolean) => {
    //     this.updateFiltersAfterSave("", savedListFilter, isShared);
    // };

    // handleSaveListFilterDialogCancel = () => {
    //     this.setState({
    //         showListFilterSaveDialog: false
    //     });
    // };
    // #endregion Saved Filter

    // #region CompanyGroup Filter
    // handleOpenCompanyGroupFilterDialog = () => {
    //     const filter = this.getActiveFilter();
    //     if (!filter) return;
    //     this.setState({
    //         showCompanyGroupFilterDialog: true,
    //         companyGroupFilterDialogFilter: filter
    //     });
    // };

    // onCompanyGroupFilterDialogOk = (closeDialog: boolean): any => {
    //     const newState = { ...this.state };
    //     this.setState({
    //         showCompanyGroupFilterDialog: !closeDialog,
    //         companyGroupFilterIsSet: this.getCompanyGroupFilterIsSet(newState)
    //     });
    //     return this.getGeneralFilterFromState(newState);
    // };
    
    // onCompanyGroupFilterDialogCancel = () => {
    //     this.setState({ showCompanyGroupFilterDialog: false });
    // };
    // #endregion CompanyGroup Filter

    handleOpenPermissions = async () => {
        const result = await AppUtils.callService(() => companyGroupService.getUnitNodePermissions(), false);
        if (!result) return;
        this.setState({
            showUnitNodePermissionsDialog: true,
            unitNodePermissions: result
        });
    };

    handlePermissionsDialogOk = () => {
        this.setState({
            dataId: Base.getRandomIntInteger(1000000),
            showUnitNodePermissionsDialog: false
        });
    };

    handlePermissionsDialogCancel = () => {
        this.setState({
            showUnitNodePermissionsDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.CompanyGroups}
                    activeCompanyId={sessionState.companyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <CompanyGroupList
                        savedFilter={state.activeSavedFilterId ? state.savedFilters.find(i => i.id === state.activeSavedFilterId) : null}
                        sessionState={sessionState}
                        dataId={state.dataId.toString(10)}
                        preloaded={true}
                        waitingPreloadedData={true}
                        list={props.companyGroupPage ? props.companyGroupPage.list : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        onRefreshSessionState={props.onRefreshSessionState}
                        onSetSortData={this.handleFilterSortData}
                        settingsActions={state.settingsActions}
                        onToggleSettingsDialog={this.handleOpenPermissions}
                        //onOpenFiltersDialog={this.handleOpenCompanyGroupFilterDialog}
                        reportTabs={
                            <FilterTabs
                                items={state.savedFilters}
                                standardFilters={state.filters}
                                selectedId={this.state.activeFilterId}
                                selectedSavedId={this.state.activeSavedFilterId}
                                onSetActiveItem={this.handleChangeActiveSavedFilter}
                                onSetActiveStandardItem={this.handleChangeActiveFilter}
                            />
                        }
                    />
                </PageContent>
                {state.showUnitNodePermissionsDialog &&
                    <UnitPermissionDialog
                        companyId={sessionState.companyId}
                        unitNodePermissions={state.unitNodePermissions}
                        onSave={companyGroupService.saveUnitNodePermissions}
                        onOk={this.handlePermissionsDialogOk}
                        onCancel={this.handlePermissionsDialogCancel}
                    />
                }
                {/* <CompanyGroupFilterDrawer
                    open={state.showCompanyGroupFilterDialog}
                    filter={state.companyGroupFilterDialogFilter}
                    authorizationLevelFilters={state.authorizationLevelFilters}
                    authorizationLevelId={state.authorizationLevelId}
                    canRemoveSharedFilters={sessionState.isCompanyAdmin}
                    onSaveFilter={this.handleSaveListFilter}
                    onRemoveFilter={this.handleRemoveSavedFilter}
                    onOk={this.onCompanyGroupFilterDialogOk}
                    onCancel={this.onCompanyGroupFilterDialogCancel}
                />
                {state.showListFilterSaveDialog &&
                    <ListFilterSaveDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        applicationPermissionType={ApplicationPermissionType.CompanyGroup}
                        listFilter={state.listFilterSaveDialogFilter}
                        generalFilter={state.listFilterSaveDialogGeneralFilter}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        reservedNames={state.listFilterSaveDialogReservedNames}
                        onGetGeneralFilter={state.listFilterSaveDialogOnGetGeneralFilter}
                        onOk={this.handleSaveListFilterDialogOk}
                        onCancel={this.handleSaveListFilterDialogCancel}
                    />
                } */}
            </PageContainer>
        );
    }
}